import { colors } from 'constants/styles/colors';
import { fontFamilyFallback } from 'constants/styles/fonts';

export const PNG_MAX_BYTES = 5 * 1024 * 1024; // 5 MB

export type PngExportFailureReason = 'no_chart' | 'render_failed' | 'file_size_exceeded';
export type PngExportResult =
  | { ok: true; blob: Blob }
  | { ok: false; reason: PngExportFailureReason };

export interface PngExportSeries {
  label: string;
  color: string;
}

export interface PngExportTeam {
  name: string;
  /** Pill background hex (from dataVisualization[colorKey].lighter). Omit to render as plain text. */
  bgColor?: string;
}

export interface PngExportOptions {
  title?: string;
  dateRange?: string;
  series?: PngExportSeries[];
  teamFilter?: PngExportTeam[];
}

// Layout constants
const PAD = 16;
const TITLE_ROW_H = 44;
const FILTER_ROW_H = 32;
const LEGEND_ROW_H = 32;
const DOT_R = 5;
const LEGEND_GAP = 20;
const PILL_H = 22;
const PILL_PAD_X = 10;
const PILL_GAP = 6;
const PILL_RADIUS = 11; // PILL_H / 2 for fully rounded ends

export const CAPTURE_SCALE = 2;

// Colors sourced from constants/styles/colors to stay in sync with the design system
const BG_COLOR = colors.fonts.white;
const TITLE_COLOR = colors.fonts.dark;
const META_COLOR = colors.fonts.darkGray;
const DIVIDER_COLOR = colors.dataVisualization.gray.dusty;

const TITLE_FONT = `bold 15px ${fontFamilyFallback}`;
const META_FONT = `12px ${fontFamilyFallback}`;

function fillRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
  ctx.fill();
}

function computeHeaderHeight(opts: PngExportOptions): number {
  return (
    (opts.title || opts.dateRange ? TITLE_ROW_H : 0) +
    (opts.teamFilter?.length ? FILTER_ROW_H : 0) +
    (opts.series?.length ? LEGEND_ROW_H : 0)
  );
}

function drawHeader(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  opts: PngExportOptions
): void {
  let y = 0;

  if (opts.title || opts.dateRange) {
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, y, canvasWidth, TITLE_ROW_H);

    if (opts.title) {
      ctx.font = TITLE_FONT;
      ctx.fillStyle = TITLE_COLOR;
      ctx.textBaseline = 'middle';
      ctx.fillText(opts.title, PAD, y + TITLE_ROW_H / 2);
    }

    if (opts.dateRange) {
      ctx.font = META_FONT;
      ctx.fillStyle = META_COLOR;
      ctx.textBaseline = 'middle';
      const tw = ctx.measureText(opts.dateRange).width;
      ctx.fillText(opts.dateRange, canvasWidth - PAD - tw, y + TITLE_ROW_H / 2);
    }

    y += TITLE_ROW_H;

    ctx.strokeStyle = DIVIDER_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PAD, y - 0.5);
    ctx.lineTo(canvasWidth - PAD, y - 0.5);
    ctx.stroke();
  }

  if (opts.teamFilter?.length) {
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, y, canvasWidth, FILTER_ROW_H);

    ctx.font = META_FONT;
    ctx.textBaseline = 'middle';

    const midY = y + FILTER_ROW_H / 2;
    const pillTop = midY - PILL_H / 2;
    const hasColoredPills = opts.teamFilter.some((t) => t.bgColor);
    const label = hasColoredPills ? 'Teams: ' : '';
    if (label) {
      ctx.fillStyle = META_COLOR;
      ctx.fillText(label, PAD, midY);
    }
    let x = PAD + (label ? ctx.measureText(label).width : 0);

    for (const team of opts.teamFilter) {
      const textW = ctx.measureText(team.name).width;

      if (team.bgColor) {
        const pillW = textW + PILL_PAD_X * 2;
        ctx.fillStyle = team.bgColor;
        fillRoundedRect(ctx, x, pillTop, pillW, PILL_H, PILL_RADIUS);
        ctx.fillStyle = TITLE_COLOR;
        ctx.fillText(team.name, x + PILL_PAD_X, midY);
        x += pillW + PILL_GAP;
      } else {
        ctx.fillStyle = META_COLOR;
        ctx.fillText(team.name, x, midY);
        x += textW + PILL_GAP;
      }
    }

    y += FILTER_ROW_H;

    ctx.strokeStyle = DIVIDER_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PAD, y - 0.5);
    ctx.lineTo(canvasWidth - PAD, y - 0.5);
    ctx.stroke();
  }

  if (opts.series?.length) {
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, y, canvasWidth, LEGEND_ROW_H);

    let x = PAD;
    ctx.font = META_FONT;
    ctx.textBaseline = 'middle';
    const midY = y + LEGEND_ROW_H / 2;

    for (const s of opts.series) {
      ctx.beginPath();
      ctx.arc(x + DOT_R, midY, DOT_R, 0, Math.PI * 2);
      ctx.fillStyle = s.color;
      ctx.fill();

      ctx.fillStyle = META_COLOR;
      ctx.fillText(s.label, x + DOT_R * 2 + 6, midY);
      x += DOT_R * 2 + 6 + ctx.measureText(s.label).width + LEGEND_GAP;
    }

    y += LEGEND_ROW_H;

    ctx.strokeStyle = DIVIDER_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PAD, y - 0.5);
    ctx.lineTo(canvasWidth - PAD, y - 0.5);
    ctx.stroke();
  }
}

/**
 * Captures the first SVG found inside `element`, draws it onto an offscreen
 * canvas below a header containing the chart title, date range, and series
 * legend, then returns the result as a PNG Blob.
 */
export async function captureChartAsPng(
  element: HTMLElement,
  opts: PngExportOptions = {}
): Promise<PngExportResult> {
  const svg = element.querySelector<SVGSVGElement>('svg');
  if (!svg) return { ok: false, reason: 'no_chart' };

  const rect = svg.getBoundingClientRect();
  const width = Math.ceil(rect.width);
  const height = Math.ceil(rect.height);
  if (!width || !height) return { ok: false, reason: 'no_chart' };

  const cloned = svg.cloneNode(true) as SVGSVGElement;
  cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  cloned.setAttribute('viewBox', `0 0 ${width} ${height}`);
  cloned.setAttribute('width', String(width * CAPTURE_SCALE));
  cloned.setAttribute('height', String(height * CAPTURE_SCALE));

  const svgString = new XMLSerializer().serializeToString(cloned);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  const headerH = computeHeaderHeight(opts);

  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(svgUrl);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width * CAPTURE_SCALE;
        canvas.height = (height + headerH) * CAPTURE_SCALE;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve({ ok: false, reason: 'render_failed' });
          return;
        }

        ctx.scale(CAPTURE_SCALE, CAPTURE_SCALE);
        ctx.fillStyle = BG_COLOR;
        ctx.fillRect(0, 0, width, height + headerH);

        drawHeader(ctx, width, opts);
        ctx.drawImage(img, 0, headerH, width, height);

        canvas.toBlob((blob) => {
          if (!blob) {
            resolve({ ok: false, reason: 'render_failed' });
            return;
          }
          if (blob.size > PNG_MAX_BYTES) {
            resolve({ ok: false, reason: 'file_size_exceeded' });
            return;
          }
          resolve({ ok: true, blob });
        }, 'image/png');
      } catch {
        resolve({ ok: false, reason: 'render_failed' });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      resolve({ ok: false, reason: 'render_failed' });
    };

    img.src = svgUrl;
  });
}

/** Create a Blob URL, trigger a browser download, then revoke the URL. */
export function downloadPngBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Build a safe filename for the downloaded PNG. */
export function buildPngFilename(chartDataType: string): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const safeName = chartDataType.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return `analytics_${safeName}_${date}.png`;
}
